import { useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { gql } from '@graphql/__generated__';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { InvoicesTable } from '@modules/subscriptions/InvoicesTable';
import { useLocale } from '@shared/hooks/locale';

const GET_USER_SUBSCRIPTIONS = gql(/* GraphQL */ `
  query GetUserSubscriptions($id: uuid!) {
    users_by_pk(id: $id) {
      id
      subscriptions(order_by: { created: desc }) {
        id
        total_amount
        status
        created
        current_period_start
        current_period_end
        invoices(order_by: { created: desc }) {
          id
          ...InvoiceFragment
        }
        invoices_aggregate {
          aggregate {
            sum {
              amount_paid
            }
          }
        }
      }
    }
  }
`);

type UserSubscriptionsProps = {
  userId: string;
};

export const UserSubscriptions = ({ userId }: UserSubscriptionsProps) => {
  const { locale } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { data, loading, error } = useQuery(GET_USER_SUBSCRIPTIONS, {
    variables: { id: userId },
  });

  useEffect(() => {
    if (error) {
      setErrorLike(error);
    }
  }, [error]);

  if (loading) {
    return <LinearProgress />;
  }

  const user = data?.users_by_pk;
  const subscriptions = user?.subscriptions ?? [];
  const invoices = subscriptions.flatMap(s => s.invoices ?? []);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        padding: 2,
      }}
    >
      <Grid size={12}>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Current period start</TableCell>
                <TableCell>Current period end</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Total paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map(subscription => (
                <TableRow key={subscription.id}>
                  <TableCell>{subscription.created.substring(0, 10)}</TableCell>
                  <TableCell>
                    {subscription.current_period_start.substring(0, 10)}
                  </TableCell>
                  <TableCell>
                    {subscription.current_period_end.substring(0, 10)}
                  </TableCell>
                  <TableCell>{subscription.status}</TableCell>
                  <TableCell align="right">
                    {Intl.NumberFormat().format(
                      subscription.total_amount / 100,
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {Intl.NumberFormat().format(
                      (subscription.invoices_aggregate?.aggregate?.sum
                        ?.amount_paid ?? 0) / 100,
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={5}>
                  <strong>Total</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {(
                      subscriptions.reduce(
                        (acc, sub) =>
                          acc +
                          (sub.invoices_aggregate.aggregate?.sum?.amount_paid ??
                            0),
                        0,
                      ) / 100
                    ).toLocaleString(locale)}
                  </strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid size={12}>
        <InvoicesTable invoices={invoices} />
      </Grid>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Grid>
  );
};
